import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Box,
  CircularProgress,
  Stack,
  styled,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  AnalysisParameters,
  BinSuggestions,
  ConsistencyResult,
  SpectralDistance,
} from './components'
import { Header } from './components/Header/Header'
import useNirResults from '../../dialogs/useNirResults'
import { useDeliveriesService } from '../../../../services'
import { usePageHeader } from '../../../../utils/hooks'
import { useSystemSettings } from '../../../../utils/hooks/useSystemSettings'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
`
const MainContent = styled(Box)`
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: auto;
`
const LoaderBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

export const IncomingDeliveriesDetails = () => {
  const { deliveryId } = useParams()
  const { getDeliveryById } = useDeliveriesService()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const { data: deliveryItem, isLoading: deliveryItemIsLoading } = useQuery({
    queryKey: ['delivery_by_id', deliveryId],
    queryFn: () => getDeliveryById(deliveryId),
    retry: false,
    cacheTime: 0,
  })

  usePageHeader({
    headerTitle: `Wheat code ${deliveryItem?.result?.wheat_code?.code || ''}`,
    showGoBack: true,
    goBackUrl: '/shipments',
  })

  const [renderNirResults, toggleNirResults] = useNirResults()

  const { isEnabled: displayStorage } = useSystemSettings('SHOW_WHEAT_STORAGE')

  const filteredBinsList = (deliveryItem?.result?.suggested_bins || [])?.filter(
    (bin) => {
      return bin?.bin?.code === '-1' ? displayStorage : true
    }
  )

  return (
    <Container gap="16px">
      {deliveryItemIsLoading ? (
        <LoaderBox>
          <CircularProgress />
        </LoaderBox>
      ) : (
        <>
          <Header
            delivery={deliveryItem?.result}
            toggleNirResults={toggleNirResults}
          />
          <MainContent
            sx={{
              flexDirection: isSmallScreen ? 'column' : 'row',
            }}
          >
            {isSmallScreen ? (
              <>
                <Stack gap="16px" flex={1}>
                  <BinSuggestions
                    discarded={deliveryItem?.result.discarded}
                    suggestedBins={filteredBinsList}
                  />
                  <AnalysisParameters delivery={deliveryItem?.result} />
                </Stack>
                <Stack flex={1} minHeight={400}>
                  <Stack flex={3} gap="16px" flexDirection="row">
                    <SpectralDistance delivery={deliveryItem?.result} />
                    <ConsistencyResult delivery={deliveryItem?.result} />
                  </Stack>
                </Stack>
              </>
            ) : (
              <>
                <Stack gap="16px" flex={1}>
                  <Stack
                    flex={3}
                    gap="16px"
                    flexDirection="row"
                    minHeight={350}
                  >
                    <SpectralDistance delivery={deliveryItem?.result} />
                    <ConsistencyResult delivery={deliveryItem?.result} />
                  </Stack>
                  <AnalysisParameters delivery={deliveryItem?.result} />
                </Stack>
                <Stack flex={1}>
                  <BinSuggestions
                    discarded={deliveryItem?.result.discarded}
                    suggestedBins={filteredBinsList}
                  />
                </Stack>
              </>
            )}
          </MainContent>
        </>
      )}
      {renderNirResults}
    </Container>
  )
}
